import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { trackPageView } from "@/lib/analytics";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import LearningPaths from "./pages/LearningPaths";
import NotFound from "./pages/NotFound";

/* Lazy-loaded heavy pages */
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const LessonPlayer = lazy(() => import("./pages/LessonPlayer"));
const MarketBreakdown = lazy(() => import("./pages/MarketBreakdown"));
const Journal = lazy(() => import("./pages/Journal"));
const AICoach = lazy(() => import("./pages/AICoach"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Membership = lazy(() => import("./pages/Membership"));
const AdminCourses = lazy(() => import("./pages/AdminCourses"));
const AdminCourseDetail = lazy(() => import("./pages/AdminCourseDetail"));
const AdminModuleEditor = lazy(() => import("./pages/AdminModuleEditor"));
const AdminLessonEditor = lazy(() => import("./pages/AdminLessonEditor"));
const AdminCreatorContent = lazy(() => import("./pages/AdminCreatorContent"));
const CreatorContentFeed = lazy(() => import("./pages/CreatorContentFeed"));
const CreatorContentDetail = lazy(() => import("./pages/CreatorContentDetail"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Contact = lazy(() => import("./pages/Contact"));

const queryClient = new QueryClient();

const Shell = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

const AdminShell = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute requireAdmin>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

const CourseRedirect = () => {
  const { courseSlug } = useParams();
  return <Navigate to={`/course/${courseSlug}`} replace />;
};

const LessonRedirect = () => {
  const { courseSlug, moduleSlug } = useParams();
  return <Navigate to={`/lesson/${courseSlug}/${moduleSlug}`} replace />;
};

const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  return null;
};

const LazyFallback = () => (
  <div className="p-6 space-y-4">
    <Skeleton className="h-8 w-48 rounded-lg" />
    <Skeleton className="h-64 w-full rounded-2xl" />
    <div className="grid grid-cols-2 gap-3">
      <Skeleton className="h-32 rounded-2xl" />
      <Skeleton className="h-32 rounded-2xl" />
    </div>
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<LazyFallback />}>
      <PageTracker />
      <Routes>
        {/* Public */}
        <Route path="/auth" element={<Auth />} />

        {/* Authenticated app */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
        <Route path="/learn" element={<Shell><LearningPaths /></Shell>} />
        <Route path="/courses" element={<Shell><LearningPaths /></Shell>} />
        <Route path="/education" element={<Shell><CreatorContentFeed /></Shell>} />
        <Route path="/education/:id" element={<Shell><CreatorContentDetail /></Shell>} />
        <Route path="/course/:courseSlug" element={<Shell><CourseDetail /></Shell>} />
        <Route path="/lesson/:courseSlug/:moduleSlug" element={<Shell><LessonPlayer /></Shell>} />
        <Route path="/module/:courseSlug/:moduleSlug" element={<Shell><LessonPlayer /></Shell>} />
        <Route path="/market-breakdown" element={<Shell><MarketBreakdown /></Shell>} />
        <Route path="/journal" element={<Shell><Journal /></Shell>} />
        <Route path="/ai-coach" element={<Shell><AICoach /></Shell>} />
        <Route path="/profile" element={<Shell><Profile /></Shell>} />
        <Route path="/settings" element={<Shell><Settings /></Shell>} />
        <Route path="/membership" element={<Shell><Membership /></Shell>} />

        {/* Admin */}
        <Route path="/admin" element={<AdminShell><AdminCourses /></AdminShell>} />
        <Route path="/admin/courses/:courseId" element={<AdminShell><AdminCourseDetail /></AdminShell>} />
        <Route path="/admin/modules/:moduleId" element={<AdminShell><AdminModuleEditor /></AdminShell>} />
        <Route path="/admin/lessons/:lessonId" element={<AdminShell><AdminLessonEditor /></AdminShell>} />
        <Route path="/admin/content" element={<AdminShell><AdminCreatorContent /></AdminShell>} />

        {/* Legal — public, no auth required */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/contact" element={<Contact />} />

        {/* Legacy redirects */}
        <Route path="/courses/:courseSlug" element={<CourseRedirect />} />
        <Route path="/courses/:courseSlug/:moduleSlug" element={<LessonRedirect />} />
        <Route path="/signals" element={<Navigate to="/dashboard" replace />} />
        <Route path="/signals/history" element={<Navigate to="/journal" replace />} />
        <Route path="/history" element={<Navigate to="/journal" replace />} />
        <Route path="/account" element={<Navigate to="/profile" replace />} />
        <Route path="/progress" element={<Navigate to="/dashboard" replace />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
